import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Mail, Send, Save, Loader2, CheckCircle2, XCircle, ExternalLink, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function EmailTab() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email-settings");
      setCfg(data);
      setDraft({});
      if (!testTo) setTestTo(user?.email || "");
    } catch {
      toast.error("Failed to load email settings");
    }
  }, [testTo, user?.email]);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const v = (k) => (draft[k] !== undefined ? draft[k] : cfg[k]);
  const set = (k, val) => setDraft((d) => ({ ...d, [k]: val }));

  const dirty = Object.keys(draft).some((k) => draft[k] !== cfg[k]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of [
        "smtp_host", "smtp_port", "smtp_user", "from_email", "from_name",
        "require_verification", "otp_expiry_minutes", "otp_max_attempts",
      ]) {
        if (draft[k] !== undefined) payload[k] = draft[k];
      }
      // Secrets: only send if non-empty
      if (draft.smtp_password) payload.smtp_password = draft.smtp_password;
      if (draft.brevo_api_key) payload.brevo_api_key = draft.brevo_api_key;
      const { data } = await api.put("/admin/email-settings", payload);
      setCfg(data);
      setDraft({});
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!testTo) return toast.error("Enter a destination email");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email-settings/test", { to_email: testTo });
      toast.success(`Test email sent via ${data.transport} in ${data.latency_ms} ms`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
      await load();
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-tab">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Email · Brevo SMTP</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your Brevo SMTP relay (preferred) and/or Brevo REST API key (fallback). Used to send signup verification OTPs and other transactional emails.
        </p>
        <a
          href="https://app.brevo.com/settings/keys/smtp"
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline"
          data-testid="brevo-smtp-link"
        >
          Get your Brevo SMTP key <ExternalLink className="h-3 w-3" />
        </a>

        <div className="mt-4 border border-border bg-muted/40 rounded-sm p-3 text-xs space-y-1.5" data-testid="brevo-help">
          <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[11px]">
            <Info className="h-3.5 w-3.5" /> Brevo gotchas (avoid 535 auth errors)
          </div>
          <ul className="list-disc list-inside text-muted-foreground space-y-0.5 pl-1">
            <li><b>SMTP user</b> is the SMTP login (e.g. <code className="font-mono">8a1b2c001@smtp-brevo.com</code>) — NOT your Brevo account email.</li>
            <li><b>SMTP password</b> is the <i>Master Password</i> / SMTP key on Brevo's SMTP & API page — NOT your account password, NOT the <code className="font-mono">xkeysib-…</code> REST API key.</li>
            <li><b>From email</b> must be a sender that's already verified in Brevo (Senders &amp; IPs → Senders).</li>
            <li>If SMTP keeps failing, paste the <code className="font-mono">xkeysib-…</code> REST API key as fallback — it works even when port 587 is blocked.</li>
          </ul>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm p-5 space-y-4" data-testid="email-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="SMTP host">
            <Input value={v("smtp_host") || ""} onChange={(e) => set("smtp_host", e.target.value)} className="rounded-sm" data-testid="smtp-host" />
          </Field>
          <Field label="SMTP port">
            <Input type="number" value={v("smtp_port") ?? 587} onChange={(e) => set("smtp_port", parseInt(e.target.value, 10))} className="rounded-sm" data-testid="smtp-port" />
          </Field>
          <Field
            label="SMTP user (Brevo SMTP login)"
            hint="Format: xxxxxxxxx@smtp-brevo.com — NOT your Brevo account email"
          >
            <Input value={v("smtp_user") || ""} onChange={(e) => set("smtp_user", e.target.value)} placeholder="8a1b2c001@smtp-brevo.com" className="rounded-sm font-mono" data-testid="smtp-user" />
          </Field>
          <Field
            label="SMTP password / master key"
            hint={cfg.smtp_password_configured ? "Configured · leave blank to keep existing" : "Brevo SMTP key (NOT your account password, NOT the xkeysib- REST key)"}
          >
            <Input
              type="password"
              value={draft.smtp_password || ""}
              onChange={(e) => set("smtp_password", e.target.value)}
              placeholder={cfg.smtp_password_configured ? "••••••••" : "Paste Brevo SMTP key"}
              autoComplete="new-password"
              className="rounded-sm font-mono"
              data-testid="smtp-password"
            />
          </Field>
          <Field
            label="Brevo REST API key (fallback)"
            hint={cfg.brevo_api_key_configured ? "Configured · leave blank to keep existing" : "Optional"}
          >
            <Input
              type="password"
              value={draft.brevo_api_key || ""}
              onChange={(e) => set("brevo_api_key", e.target.value)}
              placeholder={cfg.brevo_api_key_configured ? "••••••••" : "Optional — used if SMTP fails"}
              autoComplete="new-password"
              className="rounded-sm font-mono"
              data-testid="brevo-api-key"
            />
          </Field>
        </div>

        <div className="border-t border-border pt-4 grid sm:grid-cols-2 gap-4">
          <Field label="From email">
            <Input value={v("from_email") || ""} onChange={(e) => set("from_email", e.target.value)} placeholder="no-reply@yourdomain.com" className="rounded-sm" data-testid="from-email" />
          </Field>
          <Field label="From name">
            <Input value={v("from_name") || ""} onChange={(e) => set("from_name", e.target.value)} placeholder="Nexa" className="rounded-sm" data-testid="from-name" />
          </Field>
          <Field label="OTP expiry (minutes)">
            <Input type="number" min="1" max="60" value={v("otp_expiry_minutes") ?? 10} onChange={(e) => set("otp_expiry_minutes", parseInt(e.target.value, 10))} className="rounded-sm" data-testid="otp-expiry" />
          </Field>
          <Field label="Max OTP attempts">
            <Input type="number" min="1" max="20" value={v("otp_max_attempts") ?? 5} onChange={(e) => set("otp_max_attempts", parseInt(e.target.value, 10))} className="rounded-sm" data-testid="otp-attempts" />
          </Field>
        </div>

        <div className="border-t border-border pt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="require-verification-label">
            <input
              type="checkbox"
              checked={!!v("require_verification")}
              onChange={(e) => set("require_verification", e.target.checked)}
              className="h-4 w-4"
              data-testid="require-verification-checkbox"
            />
            <span className="font-semibold">Require email verification on signup</span>
          </label>
          <p className="text-xs text-muted-foreground mt-1 ml-6">
            When ON, new sign-ups must enter a 6-digit OTP sent to their email. Existing accounts are unaffected.
          </p>
        </div>

        <div className="border-t border-border pt-4 flex items-center gap-2 flex-wrap">
          <Button onClick={save} disabled={saving || !dirty} className="rounded-sm" data-testid="email-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            {saving ? "Saving…" : "Save settings"}
          </Button>

          <div className="ml-auto flex items-center gap-1.5">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="Send test to…"
              type="email"
              className="h-9 rounded-sm w-56 text-sm"
              data-testid="test-email-to"
            />
            <Button
              variant="outline"
              onClick={test}
              disabled={testing}
              className="rounded-sm"
              data-testid="email-test-button"
              title={!cfg.smtp_password_configured && !cfg.brevo_api_key_configured ? "Configure at least one transport first" : ""}
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send test
            </Button>
          </div>
        </div>

        {cfg.last_test_at && (
          <div className="border-t border-border pt-3 space-y-2" data-testid="last-test-result">
            <div className="text-xs font-mono flex items-center gap-2 flex-wrap">
              {cfg.last_test_ok ? (
                <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />delivered</span>
              ) : (
                <span className="text-destructive flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />failed</span>
              )}
              {cfg.last_test_transport && <span className="text-muted-foreground">via {cfg.last_test_transport}</span>}
              {cfg.last_test_latency_ms != null && <span className="text-muted-foreground">· {cfg.last_test_latency_ms} ms</span>}
              <span className="text-muted-foreground">· {new Date(cfg.last_test_at).toLocaleString()}</span>
              {cfg.last_test_error && <span className="text-destructive ml-2 truncate" title={cfg.last_test_error}>· {cfg.last_test_error}</span>}
            </div>
            {cfg.last_test_error && /535|5\.7\.8|Authentication failed/i.test(cfg.last_test_error) && (
              <div className="border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 rounded-sm p-3 text-xs flex gap-2" data-testid="auth-error-hint">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="font-semibold">Brevo rejected your credentials (535 auth failed)</div>
                  <div className="text-muted-foreground">
                    The <b>SMTP user</b> must be Brevo's SMTP login (like <code className="font-mono">8a1b2c@smtp-brevo.com</code>), and the <b>password</b> must be the SMTP <i>Master Password</i> — both shown on{" "}
                    <a href="https://app.brevo.com/settings/keys/smtp" target="_blank" rel="noreferrer" className="underline">Brevo · SMTP &amp; API</a>.
                    Your Brevo login email + account password will <b>not</b> work here.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}
